import React, { useState, useRef, useEffect } from "react";
import {
  X,
  MoreVertical,
  Trash2,
  Loader,
  Pencil,
  Check,
  X as CancelIcon,
  Search,
  FolderPlus,
  Mic,
} from "lucide-react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { useSession } from "../../context/SessionContext";
import { useModel } from "../../context/ModelContext";
import { isVoiceModel } from "../../utils/NovaVoiceManager";
import { useHat } from "../../context/HatContext";
import { useKnowledgeBase } from "../../context/KnowledgeBaseContext";
import { useUser } from "../../context/UserContext";
import {
  updateSessionTitleByUser,
  getRecentlyDeleted,
  restoreDeletedProject,
  restoreDeletedSession,
  RecentlyDeleted,
  RecentlyDeletedProject,
  RecentlyDeletedSession,
} from "../../service/apiService";
import { ProjectsService } from "./Projects";
import RecentlyDeletedList from "./RecentlyDeletedList";
import { restoreErrorMessage } from "../../utils/deletionWindow";
import { toast } from "sonner";

interface HistoryManagementProps {
  onClose: () => void;
  onAddToProject?: (sessionId: string) => void;
}

const HistoryManagement: React.FC<HistoryManagementProps> = ({
  onClose,
  onAddToProject
}) => {
  const {
    sessions,
    sessionId,
    setSessionId,
    setSessions,
    deleteSession,
    loading,
    deletingSessionId,
    loadingConversation,
    fetchAndSetSessions,
  } = useSession();
  // Every delete waits in Recently deleted before it is permanent; the view
  // lists what this user may restore (the API scopes it).
  const { userId } = useUser();
  const [view, setView] = useState<"all" | "deleted">("all");
  const [recentlyDeleted, setRecentlyDeleted] = useState<RecentlyDeleted | null>(null);
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  const [deletedError, setDeletedError] = useState(false);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const { models, setSelectedModel } = useModel();
  const { restoreSessionHat } = useHat();
  const { restoreSessionKnowledgeBase } = useKnowledgeBase();

  const [searchTerm, setSearchTerm] = useState("");
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [bouncingSessionId, setBouncingSessionId] = useState<string | null>(null);
  const [renamingSessionId, setRenamingSessionId] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [hoveredSession, setHoveredSession] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const menuRef = useRef<HTMLDivElement>(null);

  // Filter sessions based on search term
  const filteredSessions = searchTerm.trim()
    ? sessions.filter(session =>
      session.title.toLowerCase().includes(searchTerm.toLowerCase()))
    : sessions;

  // Handle clicks outside menu to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuOpenId &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setMenuOpenId(null);
      }
    };

    const handleScroll = () => {
      if (menuOpenId) {
        setMenuOpenId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    const historyContainer = document.querySelector('.history-scroll-container');
    if (historyContainer) {
      historyContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (historyContainer) {
        historyContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [menuOpenId]);

  const toggleMenu = (sessionId: string) => {
    setMenuOpenId((prevId) => (prevId === sessionId ? null : sessionId));
  };

  const loadRecentlyDeleted = async () => {
    setLoadingDeleted(true);
    try {
      setRecentlyDeleted(await getRecentlyDeleted());
      setDeletedError(false);
    } catch (error) {
      console.error("Error loading recently deleted items:", error);
      setDeletedError(true);
    } finally {
      setLoadingDeleted(false);
    }
  };

  // Load the count on open (for the toggle label) and refresh when switching to the view.
  useEffect(() => {
    loadRecentlyDeleted();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const recentlyDeletedCount = recentlyDeleted
    ? recentlyDeleted.projects.length + recentlyDeleted.sessions.length
    : null;

  // After a restore the item is live again everywhere: the sidebar's project
  // list and the viewer's own history both reload.
  const afterRestore = async () => {
    await loadRecentlyDeleted();
    ProjectsService.refresh();
    if (userId) {
      await fetchAndSetSessions(userId);
    }
  };

  const handleRestoreProject = async (project: RecentlyDeletedProject) => {
    setRestoringId(project.id);
    try {
      await restoreDeletedProject(project.id);
      toast.success("Project restored", {
        description: `${project.project_name} is back for everyone.`,
      });
      await afterRestore();
    } catch (error) {
      console.error("Error restoring project:", error);
      toast.error(restoreErrorMessage(error, "Failed to restore project"));
      // The list may be stale (a closed window): show what is there now.
      await loadRecentlyDeleted();
    } finally {
      setRestoringId(null);
    }
  };

  const handleRestoreSession = async (session: RecentlyDeletedSession) => {
    setRestoringId(session.id);
    try {
      await restoreDeletedSession(session.id);
      toast.success("Conversation restored");
      await afterRestore();
    } catch (error) {
      console.error("Error restoring conversation:", error);
      toast.error(restoreErrorMessage(error, "Failed to restore conversation"));
      // The list may be stale (a closed window): show what is there now.
      await loadRecentlyDeleted();
    } finally {
      setRestoringId(null);
    }
  };

  const handleSessionDelete = async (sessionId: string): Promise<void> => {
    await deleteSession(sessionId);
    setMenuOpenId(null);
  };

  const handleSessionClick = async (clickedSessionId: string) => {
    if (clickedSessionId !== sessionId) {
      setSessionId(clickedSessionId);
      setBouncingSessionId(clickedSessionId);

      // Find the selected session
      const selectedSession = sessions.find(
        (session) => session.id === clickedSessionId
      );

      if (selectedSession) {
        // Handle model selection if model_id exists
        if (selectedSession.model_id && models.length > 0) {
          const sessionModel = models.find(
            (model) => model.foundation_model_id === selectedSession.model_id
          );

          // A voice conversation reopens as a typed chat: Nova Sonic only speaks,
          // so the current text model stays selected (and voice mode stays shut).
          if (sessionModel && !isVoiceModel(sessionModel)) {
            setSelectedModel(sessionModel);
          }
        }

        // Show the VA and BrainBank the conversation was started with, not
        // whatever is selected right now. The session records both on its first
        // message; the contexts wait for their lists if those are still loading.
        restoreSessionHat(selectedSession.hat_id);
        restoreSessionKnowledgeBase(clickedSessionId, selectedSession.knowledge_base_id);
      }

      setTimeout(() => {
        setBouncingSessionId(null);
      }, 600);

      setSessions((prevSessions) => {
        const clickedSession = prevSessions.find(
          (session) => session.id === clickedSessionId
        );
        if (!clickedSession) return prevSessions;

        return [
          clickedSession,
          ...prevSessions.filter((session) => session.id !== clickedSessionId),
        ];
      });

      // Close the modal after selecting a session
      onClose();
    }
  };

  const startRenaming = (sessionId: string, currentTitle: string) => {
    setRenamingSessionId(sessionId);
    setNewTitle(currentTitle);
    setMenuOpenId(null);
  };

  const handleRenameSave = async (sessionId: string) => {
    try {
      await updateSessionTitleByUser(sessionId, newTitle);

      setSessions((prevSessions) =>
        prevSessions.map((session) =>
          session.id === sessionId ? { ...session, title: newTitle } : session
        )
      );

      setRenamingSessionId(null);
      toast.success("Conversation title updated successfully!");
    } catch (error) {
      console.error("Error saving session title:", error);
      toast.error("Failed to update conversation title");
    }
  };

  const handleRenameCancel = () => {
    setRenamingSessionId(null);
    setNewTitle("");
  };

  const handleTitleMouseEnter = (event: React.MouseEvent, sessionId: string, title: string) => {
    setHoveredSession(sessionId);
    updateTooltipPosition(event);
  };

  const updateTooltipPosition = (event: React.MouseEvent) => {
    setTooltipPosition({
      top: event.clientY + 20,
      left: event.clientX
    });
  };

  const handleTitleMouseLeave = () => {
    setHoveredSession(null);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  return (
    <>
      <Transition appear show={true} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 scrim" aria-hidden="true" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-100"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative bg-surface-overlay text-primary rounded-2xl px-8 py-6 text-left align-middle shadow-xl ring-1 ring-black/5 dark:ring-gray-700 transition-all w-full lg:w-[800px] sm:w-[90vw] h-[80vh] sm:h-[70vh] lg:h-[650px] overflow-hidden">
                  <div className="flex justify-between items-center border-b border-gray-200 pb-4 mb-4">
                    <h2 className="text-xl font-semibold">Chat History</h2>
                    <button
                      onClick={onClose}
                      className="rounded-full bg-transparent hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 p-2"
                    >
                      <X size={20} />
                    </button>
                  </div>

                  <div className="h-[calc(100%-5rem)] flex flex-col">
                    {/* Search bar */}
                    <div className="mb-4 relative">
                      <div className="relative flex items-center">
                        <input
                          type="text"
                          placeholder="Search conversations..."
                          value={searchTerm}
                          onChange={handleSearchChange}
                          className="w-full px-4 py-2 pr-10 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 placeholder-gray-400"
                        />
                        <Search size={18} className="absolute right-3 text-gray-400" />
                      </div>
                    </div>

                    {/* All / Recently deleted */}
                    <div className="mb-3 inline-flex self-start rounded-lg border border-gray-200 p-0.5 text-xs">
                      <button
                        type="button"
                        onClick={() => setView("all")}
                        className={`px-3 py-1.5 rounded-md font-semibold ${view === "all"
                          ? "bg-violet-50 text-violet-700"
                          : "text-gray-500 hover:text-gray-700"
                          }`}
                      >
                        All
                      </button>
                      <button
                        type="button"
                        onClick={() => setView("deleted")}
                        className={`px-3 py-1.5 rounded-md font-semibold ${view === "deleted"
                          ? "bg-amber-50 text-amber-800"
                          : "text-gray-500 hover:text-gray-700"
                          }`}
                      >
                        Recently deleted
                        {recentlyDeletedCount !== null && recentlyDeletedCount > 0 && ` · ${recentlyDeletedCount}`}
                      </button>
                    </div>

                    {/* Sessions list */}
                    <div className="flex-1 overflow-y-auto history-scroll-container pr-2"
                      style={{ maxHeight: 'calc(100% - 60px)' }}>
                      {view === "deleted" ? (
                        <RecentlyDeletedList
                          items={recentlyDeleted}
                          loading={loadingDeleted}
                          error={deletedError}
                          restoringId={restoringId}
                          searchTerm={searchTerm}
                          currentUserId={userId}
                          onRestoreProject={handleRestoreProject}
                          onRestoreSession={handleRestoreSession}
                        />
                      ) : loading ? (
                        <div className="flex justify-center items-center py-8">
                          <Loader className="animate-spin" size={24} />
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {filteredSessions.map((session) => (
                            <div
                              key={session.id}
                              className={`group relative text-sm cursor-pointer hover:bg-violet-50 p-3 rounded-lg border border-gray-100 ${sessionId === session.id
                                ? "text-violet-700 bg-violet-50 shadow-sm font-semibold border-violet-200"
                                : "text-gray-700 hover:border-gray-200"
                                } ${bouncingSessionId === session.id ? "animate-bounce" : ""
                                }`}
                            >
                              <div className="flex justify-between items-center relative">
                                {renamingSessionId === session.id ? (
                                  <div className="flex items-center flex-1 gap-2">
                                    <input
                                      type="text"
                                      className="border border-gray-300 p-2 rounded text-sm flex-1"
                                      value={newTitle}
                                      onChange={(e) => setNewTitle(e.target.value)}
                                      autoFocus
                                    />
                                    <button
                                      className="text-green-600 hover:bg-green-100 rounded-lg p-1"
                                      onClick={() => handleRenameSave(session.id)}
                                    >
                                      <Check size={16} />
                                    </button>
                                    <button
                                      className="text-red-600 hover:bg-red-100 rounded-lg p-1"
                                      onClick={handleRenameCancel}
                                    >
                                      <CancelIcon size={16} />
                                    </button>
                                  </div>
                                ) : (
                                  <div
                                    className="relative flex-1 overflow-hidden whitespace-nowrap"
                                    style={{ paddingRight: "2rem" }}
                                    onClick={() => handleSessionClick(session.id)}
                                  >
                                    <div
                                      className="overflow-hidden text-ellipsis"
                                      onMouseEnter={(e) => handleTitleMouseEnter(e, session.id, session.title)}
                                      onMouseLeave={handleTitleMouseLeave}
                                    >
                                      {session.session_type === 'voice' && (
                                        <Mic size={14} className="mr-2 inline-block" />
                                      )}
                                      {session.title}
                                    </div>
                                  </div>
                                )}

                                {renamingSessionId !== session.id && (
                                  <div
                                    className={`absolute right-0 flex items-center justify-center w-8 ${sessionId === session.id
                                      ? "opacity-100"
                                      : "opacity-0 group-hover:opacity-100"
                                      }`}
                                  >
                                    {loadingConversation && sessionId === session.id ? (
                                      <Loader className="animate-spin" size={16} />
                                    ) : deletingSessionId === session.id ? (
                                      <Loader className="animate-spin" size={16} />
                                    ) : (
                                      <MoreVertical
                                        className="cursor-pointer hover:bg-gray-200 rounded p-1"
                                        size={24}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleMenu(session.id);
                                        }}
                                      />
                                    )}
                                  </div>
                                )}
                              </div>

                              {menuOpenId === session.id && (
                                <div
                                  ref={menuRef}
                                  className="absolute right-0 top-full mt-1 p-2 w-40 rounded-lg z-50 bg-white border border-gray-200 shadow-lg"
                                >
                                  <button
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 text-gray-600 hover:text-gray-700 font-semibold rounded-md flex items-center"
                                    onClick={() => startRenaming(session.id, session.title)}
                                  >
                                    <Pencil size={14} className="mr-2" />
                                    Rename
                                  </button>

                                  {onAddToProject && (
                                    <button
                                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 text-gray-600 hover:text-gray-700 font-semibold rounded-md flex items-center"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onAddToProject(session.id);
                                        setMenuOpenId(null);
                                      }}
                                    >
                                      <FolderPlus size={14} className="mr-2" />
                                      Add to Project
                                    </button>
                                  )}

                                  <button
                                    className="w-full text-left px-3 py-2 text-sm text-red-600 hover:text-red-500 hover:bg-gray-100 font-semibold rounded-md flex items-center"
                                    onClick={() => handleSessionDelete(session.id)}
                                  >
                                    <Trash2 size={14} className="mr-2" />
                                    Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}

                          {filteredSessions.length === 0 && !loading && (
                            <div className="text-center text-gray-500 py-8">
                              <p className="text-sm">No conversations found.</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Tooltip for long titles */}
      {hoveredSession && (
        <div
          className="fixed bg-surface-overlay text-primary border border-border-default px-2 py-1 text-xs shadow-md rounded"
          style={{
            top: tooltipPosition.top + 'px',
            left: tooltipPosition.left + 'px',
            fontSize: '12px',
            borderRadius: '2px',
            zIndex: 9999,
            pointerEvents: 'none'
          }}
        >
          {sessions.find(s => s.id === hoveredSession)?.title}
        </div>
      )}
    </>
  );
};

export default HistoryManagement;